import { createContext, useCallback, useContext, useEffect, useRef, useState, type HTMLAttributes } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowDown } from "lucide-react";
import { cn } from "../../lib/cn";
import { microcopy, useDocumentLocale } from "../../lib/microcopy";

/**
 * Keeps a scrolling feed on its newest content while that content grows (a streaming answer), until the reader scrolls up to read
 * something earlier; scrolling back to the end, or `scrollToBottom`, pins it again. Only the feed's own scroll position changes.
 */
export function useStickToBottom(threshold = 72) {
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const pinned = useRef(true);
  const lastTop = useRef(0);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    if (!scrollEl) return;
    pinned.current = true;
    setAtBottom(true);
    scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior });
  }, [scrollEl]);

  useEffect(() => {
    if (!scrollEl) return;
    const onScroll = () => {
      const top = scrollEl.scrollTop;
      const near = scrollEl.scrollHeight - top - scrollEl.clientHeight <= threshold;
      if (near) pinned.current = true;
      else if (top < lastTop.current - 1) pinned.current = false;
      lastTop.current = top;
      setAtBottom(near);
    };
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [scrollEl, threshold]);

  useEffect(() => {
    if (!scrollEl || !contentEl || typeof ResizeObserver === "undefined") return;
    const follow = () => {
      if (!pinned.current) return;
      scrollEl.scrollTop = scrollEl.scrollHeight;
      lastTop.current = scrollEl.scrollTop;
    };
    follow();
    const observer = new ResizeObserver(follow);
    observer.observe(contentEl);
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [scrollEl, contentEl]);

  return { scrollRef: setScrollEl, contentRef: setContentEl, atBottom, scrollToBottom };
}

type StickToBottom = ReturnType<typeof useStickToBottom>;
const ConversationContext = createContext<StickToBottom | null>(null);

export function useConversation(): StickToBottom | null {
  return useContext(ConversationContext);
}

/** A conversation feed: `ConversationContent` scrolls and follows new content, `ConversationScrollButton` brings the reader back to it. */
export function Conversation({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  const state = useStickToBottom();
  return (
    <ConversationContext.Provider value={state}>
      <div className={cn("relative flex min-h-0 flex-1 flex-col", className)} {...props}>{children}</div>
    </ConversationContext.Provider>
  );
}

export function ConversationContent({ className, scrollClassName, children, ...props }: HTMLAttributes<HTMLDivElement> & { scrollClassName?: string }) {
  const ctx = useContext(ConversationContext);
  return (
    <div ref={ctx?.scrollRef} className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-thin", scrollClassName)}>
      <div ref={ctx?.contentRef} className={cn("flex flex-col", className)} {...props}>{children}</div>
    </div>
  );
}

export function ConversationScrollButton({ className, label }: { className?: string; label?: string }) {
  const ctx = useContext(ConversationContext);
  const locale = useDocumentLocale();
  return (
    <AnimatePresence>
      {ctx && !ctx.atBottom ? (
        <motion.div key="jump" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }} className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center">
          <button
            type="button"
            onClick={() => ctx.scrollToBottom()}
            className={cn("pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white/95 px-3 py-1.5 text-xs font-medium text-aeblack-800 shadow-sand-lg backdrop-blur transition hover:bg-sand-50", className)}
          >
            <ArrowDown className="size-3.5" /> {label ?? microcopy("jumpToLatest", locale)}
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
